import { Divider, Group, Text } from "@mantine/core";
import type { ReactNode } from "react";
import Markdown from "react-markdown";
import type { Reference } from "src/db";
import { AnswerWithSources } from "../AnswerWithSources";
import { CopyToClipboardButton } from "../CopyToClipboardButton";

export interface AnswerReviewMetadataItem {
    label: string;
    value?: ReactNode;
}

export interface AnswerReviewRequestOptions {
    datasources?: string[];
    product_line?: string | null;
    memory_enabled?: string | boolean;
}

interface AnswerReviewSectionProps {
    orgCode?: string;
    origin?: string;
    question?: string;
    answer?: string;
    references?: Reference[];
    metadata?: AnswerReviewMetadataItem[];
    requestOptions?: AnswerReviewRequestOptions;
    beforeMetadata?: ReactNode;
    renderAnswer?: (data: { answer: string; references?: Reference[] }) => ReactNode;
    questionHeading?: "h2" | "h3";
    metadataVariant?: "default" | "muted";
    children?: ReactNode;
    showDisclaimer?: boolean;
}

const renderDefaultAnswer = (answer: string, references?: Reference[], showDisclaimer = false) => {
    if (references && references.length > 0) {
        return (
            <AnswerWithSources
                answer={answer}
                references={references}
                showDisclaimer={showDisclaimer}
            />
        );
    }

    return <Markdown>{answer}</Markdown>;
};

const renderMetadata = (metadata: AnswerReviewMetadataItem[], variant: "default" | "muted") => {
    if (variant === "muted") {
        return (
            <Text size="sm" color="dimmed" mt="md">
                {metadata.map((item, index) => (
                    <span key={item.label}>
                        <strong>{item.label}:</strong> {item.value}
                        {index < metadata.length - 1 ? <br /> : null}
                    </span>
                ))}
            </Text>
        );
    }

    return metadata.map((item, index) => (
        <div key={item.label}>
            <h3 style={{ display: "inline" }}>{item.label}: &nbsp;</h3>
            {item.value}
            {index < metadata.length - 1 ? <br /> : null}
        </div>
    ));
};

const formatMemoryEnabled = (value: string | boolean | undefined) => {
    if (typeof value === "string") {
        return value.toLowerCase() === "true" ? "Yes" : "No";
    }

    return value ? "Yes" : "No";
};

const renderRequestOptions = (requestOptions: AnswerReviewRequestOptions | undefined) => {
    if (!requestOptions) {
        return null;
    }

    const datasources = Array.isArray(requestOptions.datasources)
        ? requestOptions.datasources.join(", ")
        : "";

    return (
        <>
            <Divider my="md" />
            <h3>Chat Preferences:</h3>
            <h4 style={{ display: "inline" }}>Datasources: &nbsp;</h4>
            {datasources}
            <br />
            <h4 style={{ display: "inline" }}>Product Line: &nbsp;</h4>
            {requestOptions.product_line || "All"}
            <br />
            <h4 style={{ display: "inline" }}>Memory Enabled: &nbsp;</h4>
            {formatMemoryEnabled(requestOptions.memory_enabled)}
        </>
    );
};

export const AnswerReviewSection = ({
    orgCode,
    origin,
    question,
    answer = "",
    references,
    metadata,
    requestOptions,
    beforeMetadata,
    renderAnswer,
    questionHeading = "h3",
    metadataVariant = "default",
    children,
    showDisclaimer = false,
}: AnswerReviewSectionProps) => {
    const HeadingComponent = questionHeading;
    const metadataItems = metadata?.filter(
        (item) => item.value !== undefined && item.value !== null,
    );
    const answerContent = renderAnswer
        ? renderAnswer({ answer, references })
        : renderDefaultAnswer(answer, references, showDisclaimer);

    return (
        <>
            {orgCode ? (
                <Group gap="xs" mb="xs">
                    <Text>
                        <strong>Organization:</strong> {orgCode}
                    </Text>
                    <CopyToClipboardButton content={orgCode} size="xs" />
                </Group>
            ) : null}

            {origin ? (
                <Group gap="xs" mb="xs">
                    <Text>
                        <strong>System Origin:</strong> {origin}
                    </Text>
                    <CopyToClipboardButton content={origin} size="xs" />
                </Group>
            ) : null}

            {question ? (
                <Group gap="xs" mb="md">
                    <Text>
                        <HeadingComponent style={{ display: "inline" }}>
                            Question: &nbsp;
                        </HeadingComponent>
                        {question}
                    </Text>
                    <CopyToClipboardButton content={question} size="xs" />
                </Group>
            ) : null}
            <Divider my="md" />
            {answerContent}
            {beforeMetadata}
            {metadataItems && metadataItems.length > 0 ? (
                <>
                    <Divider my="md" />
                    {renderMetadata(metadataItems, metadataVariant)}
                </>
            ) : null}
            {renderRequestOptions(requestOptions)}
            {children}
        </>
    );
};
